import {
  Box,
  Button,
  Heading,
  IconButton,
  Link,
  Skeleton,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useCallbackRef,
  useMediaQuery,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCopyToClipboard } from 'react-use';

import CopyIcon from '@/assets/icons/ic-copy.svg';

import { QuestionsAndAnswers } from '@/components/ui/features/conversation/QuestionsAndAnswers/QuestionsAndAnswers';
import { RocketchatSidebar } from '@/components/ui/features/conversation/RocketchatSidebar/RocketchatSidebar';
import { VotingContent } from '@/components/ui/features/events-app/event/EventContent/components/VotingContent';
import { LectureSpeakersList } from '@/components/ui/features/events-app/event/LectureSpeakers/LectureSpeakersList';
import { ResourceDownload } from '@/components/ui/features/events-app/event/ResourceDownload/ResourceDownload';
import { SideBarMessage } from '@/components/ui/features/events-app/event/SideBarMessage/SideBarMessage';
import {
  ContentContainer,
  Header,
  InformationContainer,
  Root,
  SidebarContainer,
} from '@/containers/Event/EventContent.elements';
import { useAnalytics } from '@/context/Analytics';
import { ChatState } from '@/enums/ChatState';
import { ResourceType } from '@/enums/index';
import { IToastType } from '@/enums/ToastType';
import { VideoType } from '@/enums/VideoType';
import useSession from '@/hooks/useSession';
import { useToast } from '@/hooks/useToast';
import { useResourceRelationship, useResources } from '@/lib/datx-jsonapi-react';
import { queryThinEventConfigurationRelationship } from '@/queries/thin-events';
import { queryTranscriptsByLectureId } from '@/queries/transcripts';
import { EventConfiguration } from '@/resources/EventConfiguration';
import { ThinEvent } from '@/resources/ThinEvent';
import { ThinEventSession } from '@/resources/ThinEventSession';
import { ThinLecture } from '@/resources/ThinLecture';
import { IVideoEventAnalytics } from '@/utils/analytics';

import { AbstractContent } from './components/AbstractContent';
import { PdfContent } from './components/PdfContent';
import { VideoContent } from './components/VideoContent';

interface IEventContentLecture {
  lecture: ThinLecture;
  event: ThinEvent;
  eventSession: ThinEventSession;
}

function getTabIndexByQueryParam(name: string | Array<string>, tabList: Array<ITabProps>) {
  if (!name) return 0;
  const tabListIndex = tabList.findIndex((e) => e.queryParam === name.toString());

  return tabListIndex === -1 ? 0 : tabListIndex;
}

export const EventContentLecture: FC<IEventContentLecture> = ({ lecture, event, eventSession }) => {
  const { t } = useTranslation();
  const { user } = useSession();
  const analytics = useAnalytics();
  const router = useRouter();
  const [isDesktop] = useMediaQuery('(min-width: 1024px)');
  const [_, copyToClipboard] = useCopyToClipboard();
  const toast = useToast();

  const { data: eventConfiguration } = useResourceRelationship<EventConfiguration>(
    queryThinEventConfigurationRelationship(event.id)
  );

  const { data: transcripts } = useResources(queryTranscriptsByLectureId(lecture.id));

  const abstractTabLabel =
    (eventConfiguration?.lecturesAbstractPage && eventConfiguration?.lecturesAbstractPageLabel) ??
    t('lectureTabs.abstractTab');

  const tabList: Array<ITabProps> = useMemo(
    () =>
      [
        {
          shouldRender: Boolean(lecture.playlist),
          tabLabel: t('lectureTabs.videoTab'),
          queryParam: t('lectureTabs.videoTab'),
        },
        {
          shouldRender: Boolean(lecture.posterDocument?.url),
          tabLabel: t('lectureTabs.posterTab'),
          queryParam: t('lectureTabs.posterTab'),
        },
        {
          shouldRender: Boolean(lecture.abstract && abstractTabLabel),
          tabLabel: abstractTabLabel,
          queryParam: t('lectureTabs.abstractTab'),
        },
        {
          shouldRender: !isDesktop,
          tabLabel: t('lectureTabs.questionsTab'),
          queryParam: t('lectureTabs.questionsTab'),
        },
        {
          shouldRender: Boolean(lecture.voting),
          tabLabel: eventConfiguration?.lecturesVotingLabel || t('lectureTabs.votingTab'),
          queryParam: t('lectureTabs.votingTab'),
        },
      ].filter((tl) => tl.shouldRender),
    [abstractTabLabel, isDesktop, lecture, t, eventConfiguration]
  );

  const tabIndex = getTabIndexByQueryParam(router.query.tab, tabList);

  const videoAnalytics = {
    event_id: event.id,
    event_name: event.name,
    lecture_id: lecture.id,
    lecture_name: lecture.title,
    session_name: eventSession.name,
    session_id: eventSession.id,
    speaker_name: lecture.positionSortedProfiles.map((profile) => profile.fullName).join(', '),
    video_type: VideoType.Session,
  } as IVideoEventAnalytics;

  const handleTabsChange = (index: number) => {
    router.replace({ query: { ...router.query, tab: tabList[index].queryParam } });
  };

  const onMessageSend = useCallbackRef((message) => {
    if (message.author === user.email) {
      if (message.attributes.parentMessageId) {
        analytics.track('event_session_question_reply_send');
      } else {
        analytics.track('event_session_question_send');
      }
    }
  });

  if (!transcripts || !eventConfiguration) {
    return <Skeleton />;
  }

  const doiLink = lecture.underlineDoi ? `https://doi.org/${lecture.underlineDoi}` : null;

  function handleCopyToClipboard() {
    if (!doiLink) return;
    copyToClipboard(doiLink);
    toast({ status: IToastType.SUCCESS, title: 'Copied to clipboard' });
  }

  return (
    <Root>
      <ContentContainer>
        <Header>
          <InformationContainer>
            <Box>
              <Heading fontSize="xl">{lecture.title}</Heading>

              <LectureSpeakersList profiles={lecture.positionSortedProfiles} />
              {doiLink && (
                <Text lineHeight="1.25rem" fontSize="1rem" mt={2}>
                  doi:{' '}
                  <Link target="_blank" href={doiLink}>
                    {doiLink}
                  </Link>
                  <IconButton
                    onClick={handleCopyToClipboard}
                    aria-label="copy"
                    ml="2"
                    variant="unstyled"
                    icon={<CopyIcon width="24px" />}
                  />
                </Text>
              )}
            </Box>
            <Box>
              <ResourceDownload lecture={lecture} />
              {lecture.gatherTownUrl && (
                <Button
                  as="a"
                  my={4}
                  href={lecture.gatherTownUrl}
                  target="_blank"
                  size="sm"
                  w="192px"
                  display="flex"
                  justifyContent="space-between"
                  colorScheme="black"
                >
                  {t('gatherTown.buttonLabel')}
                </Button>
              )}
            </Box>
          </InformationContainer>
        </Header>

        <Box flexGrow={1}>
          <Tabs
            isLazy
            size="sm"
            h="full"
            display="flex"
            flexDirection="column"
            overflow="hidden"
            index={tabIndex}
            onChange={handleTabsChange}
          >
            <TabList px={6} py={0}>
              {tabList.map((tl) => (
                <Tab key={tl.tabLabel} py={2}>
                  {tl.tabLabel}
                </Tab>
              ))}
            </TabList>
            <TabPanels h="full">
              {lecture.playlist && (
                <TabPanel p={0}>
                  <VideoContent
                    title={lecture.title}
                    analytics={videoAnalytics}
                    poster={lecture.largePosterUrl}
                    transcripts={transcripts}
                    source={lecture.playlist}
                  />
                </TabPanel>
              )}
              {lecture.posterDocument?.url && (
                <TabPanel h="full" p={4}>
                  <PdfContent pdfUrl={lecture.posterDocument?.url} />
                </TabPanel>
              )}
              {lecture.abstract && abstractTabLabel && (
                <TabPanel h="full" p={4}>
                  <AbstractContent content={lecture.abstract} />
                </TabPanel>
              )}
              {isDesktop ? null : (
                <TabPanel h="500px" py={0} p={4}>
                  {lecture.chatState === ChatState.EventApp && (
                    <QuestionsAndAnswers
                      onMessageSend={onMessageSend}
                      resourceId={lecture.id}
                      conversationSid={lecture.twilioQuestionsChannel}
                      resourceType={ResourceType.Lecture}
                    />
                  )}
                  {lecture.chatState === ChatState.RocketChat && <RocketchatSidebar url={lecture.rocketchatUrl} />}
                </TabPanel>
              )}
              {lecture.voting && (
                <TabPanel py={0} p={4}>
                  <VotingContent lecture={lecture} />
                </TabPanel>
              )}
            </TabPanels>
          </Tabs>
        </Box>
      </ContentContainer>
      {isDesktop ? (
        <SidebarContainer>
          {lecture.chatState === ChatState.EventApp && (
            <QuestionsAndAnswers
              onMessageSend={onMessageSend}
              resourceId={lecture.id}
              resourceType={ResourceType.Lecture}
              conversationSid={lecture.twilioQuestionsChannel}
            />
          )}
          {lecture.chatState === ChatState.Library && (
            <SideBarMessage>
              <NextLink href={`/lecture/${router.query.lectureId}`} passHref legacyBehavior>
                <Button
                  style={{
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                  }}
                  variant="link"
                >
                  {t('lecture.chatAtLibrary')}
                </Button>
              </NextLink>
            </SideBarMessage>
          )}
          {lecture.chatState === ChatState.RocketChat && <RocketchatSidebar url={lecture.rocketchatUrl} />}
        </SidebarContainer>
      ) : null}
    </Root>
  );
};

interface ITabProps {
  shouldRender: boolean;
  tabLabel: string;
  queryParam: string;
}
